import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import useApi from "../../hooks/useApi";

import Loader from "../../Components/Loader";
import ModalConfirmation from "../../Components/ModalConfirmation";
import ModalWarning from "../../Components/ModalWarning";
import { osPlatform } from "../../store/GlobalVarriables";

import { ReactComponent as Tick } from "../../static/images/tick_sign.svg";

import "./style.scss";

const InvoiceDetails = () => {
  const { get, put, loading } = useApi();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    consumerName,
    sessions,
    eventsCount,
    externalId,
    amount,
    currency,
    dueDate,
    overDue,
    invoiceId,
    userRole,
  } = location.state || {};

  const [confirmModalToggle, setConfirmModalToggle] = useState();
  const [confirmModalText, setConfirmModalText] = useState();

  const [warningModalToggle, setWarningModalToggle] = useState("");
  const [warningModalText, setWarningModalText] = useState("");

  const [StatusIdentifier, setStatusIdentifier] = useState();

  const [dynamicStatus, setDynamicStatus] = useState();
  const [dynamicActivities, setDynamicActivities] = useState();

  const handleInvoiceStatus = async () => {
    if (StatusIdentifier === "mark-paid") {
      try {
        const data = await put(`/Invoice`, {
          id: invoiceId,
          status: "Paid",
        });

        setWarningModalToggle("modal-warning");
        setWarningModalText(t("marked_paid_warning_modaltext"));
      } catch (error) {
        console.log(error);
      }
    } else {
      try {
        const data = await put(`/Invoice`, {
          id: invoiceId,
          status: "Open",
        });
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("marked_unpaid_warning_modaltext"));
      } catch (error) {
        console.log(error);
      }
    }
    setStatusIdentifier("");
  };

  const convertDate = (value, identity) => {
    const date = new Date(value);

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const monthName = t(`months.${month}`); // i18next will get correct localized month

    const formattedDate = `${day} ${monthName} ${year}`;

    const timePart = date.toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    if (identity === "time") {
      return `${formattedDate} - ${timePart}`;
    } else if (identity === "year") {
      return formattedDate;
    } else if (identity === "month") {
      return `${day} ${monthName}`;
    }
  };

  const getStatus = async () => {
    const ps10005000 = JSON.parse(localStorage.getItem("ps10005000"));
    const accountID = jwtDecode(ps10005000).buid;

    try {
      const data = await get(`/BusinessAccount/${accountID}/Invoices`);

      let status =
        userRole === "Provider"
          ? data.invoices.find((item) => item.externalId === externalId)?.status
          : data.bills.find((item) => item.externalId === externalId)?.status;

      if (userRole === "Provider") {
        let activites = data.invoices.find(
          (item) => item.externalId === externalId,
        )?.activites.statusUpdates;

        activites?.sort(
          (a, b) => new Date(b.activityTime) - new Date(a.activityTime),
        );
        setDynamicActivities(activites);
      }

      setDynamicStatus(status);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getStatus();
  }, []);

  return (
    <div className="invoice-details">
      <ModalConfirmation
        changeModal={setConfirmModalToggle}
        modalValue={confirmModalToggle}
        modalText={confirmModalText}
        confirm={handleInvoiceStatus}
      />
      <ModalWarning
        changeModal={setWarningModalToggle}
        modalValue={warningModalToggle}
        warningText={warningModalText}
        buttonText={t("close")}
        Image={Tick}
        handleButton={() => {
          getStatus();
        }}
      />
      <div className="invocie-header">
        <div className="invoice-id">
          <span>{t("invoice_details")}</span>
          <span>#{externalId}</span>
        </div>
        <h4 className="consumer-name">{consumerName}</h4>
      </div>
      <div className="invocie-summary">
        <h3>{t("summary")}</h3>
        <div className="total-sessions">
          <span>{t("total_sessions")}</span>
          <span>{eventsCount}</span>
        </div>
        <div className="total-amount">
          <span>{t("total_amount")}</span>
          <span>{amount + " " + currency}</span>
        </div>
        <div className="status">
          <span>{t("due")}</span>

          {dynamicStatus === "Paid" ? (
            <span id="paid">{t("paid")}</span>
          ) : dynamicStatus === "Open" && overDue === true ? (
            <span id="due">
              {i18n.language === "ka" ? "" : t("due_on") + " "}{" "}
              {convertDate(dueDate, "month")}
            </span>
          ) : (
            <span id="open">
              {i18n.language === "ka" ? "" : t("due_on") + " "}{" "}
              {convertDate(dueDate, "month")}
            </span>
          )}
        </div>
      </div>
      <div className="invocie-sessions">
        <h3>{t("sessions")}</h3>

        <ul>
          {sessions.map((session) => (
            <li key={session}>
              <span>{convertDate(session, "time")}</span>
            </li>
          ))}
        </ul>
      </div>
      {userRole === "Provider" ? (
        <div className="invoice-activity">
          <h3>{t("activity")}</h3>
          <ul>
            {dynamicActivities?.map((activity) => (
              <li key={activity.activityTime}>
                <span>{t(activity.code.toLowerCase()) + " "}</span>
                <span>{convertDate(activity.activityTime, "year")}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        className="invocie-button-container"
        style={{
          bottom: osPlatform === "iOS" ? "80px" : "65px",
        }}
      >
        <button
          id="go-back"
          onClick={() => {
            navigate(-1);
          }}
          style={{ width: userRole === "Provider" ? "48%" : "60%" }}
        >
          {t("go_back")}
        </button>

        {userRole === "Provider" ? (
          <>
            {dynamicStatus === "Paid" ? (
              <button
                id="paid-bt"
                onClick={() => {
                  setConfirmModalText(t("mark_unpaid_confirm_modaltext"));
                  setConfirmModalToggle("modal-confirm");
                  setStatusIdentifier("mark-unpaid");
                }}
              >
                {t("mark_unpaid")}
              </button>
            ) : (
              <button
                id="unpaid-bt"
                onClick={() => {
                  setConfirmModalText(t("mark_paid_confirm_modaltext"));
                  setConfirmModalToggle("modal-confirm");
                  setStatusIdentifier("mark-paid");
                }}
              >
                {t("mark_paid")}
              </button>
            )}
          </>
        ) : null}
      </div>
      {loading && <Loader />}
    </div>
  );
};

export default InvoiceDetails;
